import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement, ArcElement, RadialLinearScale } from 'chart.js';
import { FiFlag, FiCheckCircle, FiClock, FiTarget, FiTrendingUp, FiCalendar, FiArrowRight, FiChevronRight,
         FiActivity, FiWind, FiDroplet, FiAward, FiBarChart2, FiGlobe, FiRefreshCw, FiSun } from 'react-icons/fi';
import { FaWineBottle, FaWineGlass, FaLeaf } from 'react-icons/fa';
import ResponsiveChart from '../components/ResponsiveChart';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale
);

const Tracking: React.FC = () => {
  // Animation states
  const [animate, setAnimate] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [selectedYear, setSelectedYear] = useState('2023');
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  // Apply animation after component mounts
  useEffect(() => {
    setAnimate(true);

    // Add scroll animation to chart
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && chartRef.current) {
          chartRef.current.classList.add('scale-100', 'opacity-100');
          chartRef.current.classList.remove('scale-95', 'opacity-0');
        }
      },
      { threshold: 0.1 }
    );

    const observedElement = chartRef.current;
    if (observedElement) {
      observer.observe(observedElement);
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement);
      }
    };
  }, []);

  // Sample data for the timeline
  const timelineData = {
    labels: [
      'Project Planning',
      'Vineyard Setup',
      'Harvest Planning',
      'Fermentation Process',
      'Aging Process',
      'Quality Control',
      'Bottling',
      'Distribution'
    ],
    datasets: [
      {
        label: 'Premium Line',
        data: [100, 85, 70, 60, 40, 30, 20, 10],
        backgroundColor: '#B4233A',
        borderColor: '#B4233A',
        borderWidth: 1,
      },
      {
        label: 'Standard Line',
        data: [100, 90, 85, 75, 60, 50, 30, 15],
        backgroundColor: '#B77A2A',
        borderColor: '#B77A2A',
        borderWidth: 1,
      },
      {
        label: 'Economy Line',
        data: [100, 95, 90, 80, 70, 65, 45, 25],
        backgroundColor: '#3F8A4E',
        borderColor: '#3F8A4E',
        borderWidth: 1,
      },
      {
        label: 'Target',
        data: [100, 100, 100, 100, 100, 100, 100, 100],
        backgroundColor: 'rgba(209, 213, 219, 0.3)',
        borderColor: 'rgba(209, 213, 219, 0.6)',
        borderWidth: 1,
      }
    ],
  };

  const chartOptions = {
    indexAxis: 'y' as const,
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Completion Percentage (%)',
          font: { family: "'Figtree', sans-serif" },
        },
        grid: {
          color: 'rgba(43, 33, 29, 0.08)',
          // @ts-ignore
          borderDash: [4, 4],
        },
        border: { display: false },
        ticks: { color: '#7A6A5F', font: { family: "'Figtree', sans-serif" } },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#7A6A5F', font: { family: "'Figtree', sans-serif" } },
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
          font: { size: 12, family: "'Figtree', sans-serif" },
        },
      },
      title: {
        display: false,
        text: 'Wine Production Timeline Progress',
        font: {
          size: 16,
          weight: 'bold' as const,
          family: "'Figtree', sans-serif",
        },
        color: '#2B211D',
        padding: { bottom: 20 },
      },
      tooltip: {
        backgroundColor: 'rgba(37, 19, 22, 0.95)',
        titleColor: 'white',
        bodyColor: 'rgba(255, 255, 255, 0.85)',
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${context.parsed.x}%`;
          }
        }
      }
    },
    animation: {
      duration: 2000
    }
  };

  // Quality metrics data
  const qualityData = {
    labels: ['Premium', 'Standard', 'Economy'],
    datasets: [
      {
        data: [60, 30, 10],
        backgroundColor: ['#B4233A', '#3F718A', '#B77A2A'],
        borderColor: ['#B4233A', '#3F718A', '#B77A2A'],
        borderWidth: 1,
      },
    ],
  };

  const qualityOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
          font: { size: 12, family: "'Figtree', sans-serif" },
        },
      },
      title: {
        display: false,
        text: 'Production Quality Distribution',
        font: {
          size: 16,
          weight: 'bold' as const,
          family: "'Figtree', sans-serif",
        },
        color: '#2B211D',
      },
      tooltip: {
        backgroundColor: 'rgba(37, 19, 22, 0.95)',
        titleColor: 'white',
        bodyColor: 'rgba(255, 255, 255, 0.85)',
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `${context.label}: ${context.parsed}%`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true
    }
  };

  // Milestones to display on the timeline
  const milestones = [
    {
      title: 'Project Kickoff',
      date: 'January 2023',
      status: 'Completed',
      description: 'Project planning and initial setup completed',
      icon: FiCheckCircle,
      iconColor: 'text-green-500',
      details: 'Completed comprehensive business plan, secured initial funding, and established key partnerships with local growers.',
      achievements: ['Business plan approved', 'Initial funding secured', 'Key partnerships established']
    },
    {
      title: 'Vineyard Management',
      date: 'March 2023 - Present',
      status: 'In Progress',
      description: 'Setting up vineyard blocks and tracking treatments',
      icon: FiClock,
      iconColor: 'text-primary-500',
      details: 'Currently implementing vineyard management systems and establishing tracking protocols for different grape varieties.',
      achievements: ['System architecture designed', '75% of vineyard blocks mapped', 'Treatment tracking implemented']
    },
    {
      title: 'Harvest Season',
      date: 'September 2023',
      status: 'Upcoming',
      description: 'First harvest and fermentation process',
      icon: FiTarget,
      iconColor: 'text-amber-500',
      details: 'Planning for first harvest season including equipment preparation, staff training, and logistics coordination.',
      achievements: ['Equipment ordered', 'Harvest plan drafted', 'Staff recruitment initiated']
    },
    {
      title: 'First Release',
      date: 'June 2024',
      status: 'Target',
      description: 'First bottling and product release',
      icon: FiFlag,
      iconColor: 'text-red-500',
      details: 'Targeting our first commercial release with initial focus on premium varietals and direct-to-consumer sales channels.',
      achievements: ['Label design initiated', 'Distribution channels researched', 'Marketing plan drafted']
    },
  ];

  // Recent activities
  const recentActivities = [
    {
      id: 1,
      action: 'Vineyard Block A23 treatment completed',
      timestamp: '2 hours ago',
      user: 'Emily Johnson',
      type: 'treatment'
    },
    {
      id: 2,
      action: 'Fermentation started for Batch #2023-12',
      timestamp: '1 day ago',
      user: 'Robert Smith',
      type: 'fermentation'
    },
    {
      id: 3,
      action: 'New quality testing results added',
      timestamp: '2 days ago',
      user: 'Jessica Williams',
      type: 'quality'
    },
    {
      id: 4,
      action: 'Barrel rotation completed for aging room 3',
      timestamp: '3 days ago',
      user: 'Michael Brown',
      type: 'aging'
    },
  ];

  // Production goals data
  const productionGoals = [
    {
      title: 'Cases Target',
      value: '5,000',
      icon: FiTarget,
      iconBg: 'bg-primary-500',
      valueBg: 'text-primary-500',
      description: 'Annual production target for our premium wine lines',
      progress: 18,
      projectedTarget: {
        year: '2024',
        value: '5,000'
      }
    },
    {
      title: 'Varietals',
      value: '12',
      icon: FiWind,
      iconBg: 'bg-emerald-500',
      valueBg: 'text-emerald-600',
      description: 'Different grape varieties in production this season',
      progress: 100,
      projectedTarget: {
        year: '2023',
        value: '12'
      }
    },
    {
      title: 'Release Date',
      value: '2024',
      icon: FiFlag,
      iconBg: 'bg-amber-500',
      valueBg: 'text-amber-600',
      description: 'Target year for our first major wine release',
      progress: 42,
      projectedTarget: {
        year: '2024 Q2',
        value: 'Jun'
      }
    },
  ];

  // Years for timeline filtering
  const years = ['2023', '2024', '2025'];

  // Helper function to get activity icon
  const getActivityIcon = (type: string) => {
    switch(type) {
      case 'treatment':
        return FiActivity({ size: 18, className: "text-emerald-500" });
      case 'fermentation':
        return FaWineBottle({ size: 18, className: "text-primary-500" });
      case 'quality':
        return FiCheckCircle({ size: 18, className: "text-info-500" });
      case 'aging':
        return FaWineGlass({ size: 18, className: "text-amber-500" });
      default:
        return FiClock({ size: 18, className: "text-monday-text-muted" });
    }
  };

  return (
    <div className={`space-y-6 transition-opacity duration-700 ${animate ? 'opacity-100' : 'opacity-0'}`}>
      {/* Decorative background elements */}
      <div className="absolute top-20 right-5 w-24 h-24 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
      <div className="absolute top-32 left-10 w-36 h-36 bg-secondary-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-32 right-20 w-32 h-32 bg-success-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>

      {/* Header with tabs */}
      <div
        className="relative overflow-hidden flex flex-col md:flex-row md:justify-between items-start md:items-center bg-white/95 border border-monday-border-light rounded-2xl shadow p-6"
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <div className="z-10">
          <h1 className="text-3xl font-extrabold tracking-tight text-monday-text mb-2 flex items-center">
            {FiBarChart2({ size: 28, className: "mr-3 text-primary-500" })}
            <span className="relative">
              Production Tracking
              <span className="absolute -bottom-1 left-0 w-full h-1 bg-secondary-400 transform transition-transform duration-300 ease-out origin-left" style={{ transform: isHeaderHovered ? 'scaleX(1)' : 'scaleX(0)' }}></span>
            </span>
          </h1>
          <p className="text-monday-text-muted text-base">Monitoring our journey from vineyard to bottle</p>
        </div>

        <div className="flex space-x-1 mt-4 md:mt-0 z-10 bg-monday-header border border-monday-border-light p-1 rounded-full">
          <button
            className={`px-4 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ease-in-out flex items-center ${
              activeTab === 'timeline'
              ? 'bg-primary-500 text-white shadow-primary'
              : 'text-monday-text-muted hover:bg-white hover:text-monday-text'
            }`}
            onClick={() => setActiveTab('timeline')}
          >
            {FiTrendingUp({ size: 16, className: activeTab === 'timeline' ? 'mr-2 text-primary-500' : 'mr-2' })}
            Timeline View
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ease-in-out flex items-center ${
              activeTab === 'quality'
              ? 'bg-primary-500 text-white shadow-primary'
              : 'text-monday-text-muted hover:bg-white hover:text-monday-text'
            }`}
            onClick={() => setActiveTab('quality')}
          >
            {FiAward({ size: 16, className: activeTab === 'quality' ? 'mr-2 text-primary-500' : 'mr-2' })}
            Quality Metrics
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ease-in-out flex items-center ${
              activeTab === 'activity'
              ? 'bg-primary-500 text-white shadow-primary'
              : 'text-monday-text-muted hover:bg-white hover:text-monday-text'
            }`}
            onClick={() => setActiveTab('activity')}
          >
            {FiActivity({ size: 16, className: activeTab === 'activity' ? 'mr-2 text-primary-500' : 'mr-2' })}
            Recent Activity
          </button>
        </div>

        {/* Decorative elements */}
        <div className="absolute -right-12 -top-12 w-40 h-40 bg-primary-50 rounded-full"></div>
        <div className="absolute -right-6 -top-6 w-20 h-20 bg-secondary-50 rounded-full"></div>
        <div className="absolute -left-16 -bottom-10 w-32 h-32 bg-success-50 rounded-full"></div>
      </div>

      {activeTab === 'timeline' && (
        <>
          {/* Timeline View Content */}
          <div className="bg-white/95 rounded-2xl shadow border border-monday-border-light overflow-hidden relative">
            {/* Decorative background elements */}
            <div className="absolute -top-5 right-0 w-32 h-32 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>
            <div className="absolute bottom-5 left-0 w-20 h-20 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-20"></div>

            <div className="p-6 relative z-10">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <div className="mb-4 sm:mb-0">
                  <h2 className="text-xl font-bold text-monday-text mb-2 inline-flex items-center">
                    {FiBarChart2({ className: "mr-2 text-primary-500", size: 20 })}
                    Production Timeline Tracker
                  </h2>
                  <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                </div>
                <div className="flex space-x-1 bg-monday-header rounded-md p-1 shadow-inner">
                  {years.map(year => (
                    <button
                      key={year}
                      className={`px-3 py-1.5 text-sm font-medium rounded transition-all duration-300 ${
                        selectedYear === year
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md transform scale-105'
                          : 'text-monday-text hover:bg-monday-hover'
                      }`}
                      onClick={() => setSelectedYear(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-monday-text-muted mb-6 max-w-3xl">
                Track the wine production lifecycle from vineyard to bottle. This visualization shows our current progress against targets for each phase of the production process.
              </p>

              <div
                ref={chartRef}
                className="h-96 mb-8 transition-all duration-1000 ease-out transform scale-95 opacity-0 bg-white p-4 rounded-xl shadow-xs border border-monday-border-light"
              >
                <ResponsiveChart type="bar" data={timelineData as any} options={chartOptions as any} height="360px" variant="embedded" />
              </div>

              {/* Time period selector pills */}
              <div className="flex flex-wrap gap-2 items-center justify-center mb-4">
                <span className="text-sm text-monday-text-muted mr-2">View by:</span>
                {['Quarter', 'Month', 'Week'].map((period) => (
                  <button
                    key={period}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      period === 'Month' ? 'bg-primary-100 text-primary-600 border border-primary-200' : 'bg-monday-header text-monday-text-muted hover:bg-monday-hover'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>

              {/* Add process flow diagram */}
              <div className="mt-8 mb-4">
                <h3 className="text-sm uppercase tracking-wide text-monday-text-muted mb-4">Production Process Flow</h3>
                <div className="overflow-x-auto pb-4">
                  <div className="min-w-[700px]">
                    <div className="relative flex flex-col space-y-8">
                      {/* Product Line 1 - Premium */}
                      <div className="relative flex items-center justify-between py-2">
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-neutral-200 -translate-y-1/2 transform"></div>
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-gray-200 -translate-y-1/2 transform" style={{ width: '40%' }}></div>

                        <div className="absolute left-0 -top-6 text-sm font-medium text-green-700">Premium Line</div>

                        {['Planning', 'Vineyard', 'Harvest', 'Fermentation', 'Aging', 'Bottling', 'Distribution'].map((step, index) => {
                          const isActive = index < 4;
                          return (
                            <div key={`premium-${step}`} className="z-10 flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow ${
                                isActive ? 'bg-green-500 text-white' : 'bg-white text-monday-text-muted border border-monday-border-light'
                              }`}>
                                {index + 1}
                              </div>
                              <span className={`mt-2 text-xs ${isActive ? 'text-green-700 font-medium' : 'text-monday-text-muted'}`}>{step}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Product Line 2 - Standard */}
                      <div className="relative flex items-center justify-between py-2">
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-neutral-200 -translate-y-1/2 transform"></div>
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-monday-border-light -translate-y-1/2 transform" style={{ width: '60%' }}></div>

                        <div className="absolute left-0 -top-6 text-sm font-medium text-primary-600">Standard Line</div>

                        {['Planning', 'Vineyard', 'Harvest', 'Fermentation', 'Aging', 'Bottling', 'Distribution'].map((step, index) => {
                          const isActive = index < 5;
                          return (
                            <div key={`standard-${step}`} className="z-10 flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow ${
                                isActive ? 'bg-primary-500 text-white' : 'bg-white text-monday-text-muted border border-monday-border-light'
                              }`}>
                                {index + 1}
                              </div>
                              <span className={`mt-2 text-xs ${isActive ? 'text-primary-600 font-medium' : 'text-monday-text-muted'}`}>{step}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Product Line 3 - Economy */}
                      <div className="relative flex items-center justify-between py-2">
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-neutral-200 -translate-y-1/2 transform"></div>
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-gray-200 -translate-y-1/2 transform" style={{ width: '70%' }}></div>

                        <div className="absolute left-0 -top-6 text-sm font-medium text-amber-700">Economy Line</div>

                        {['Planning', 'Vineyard', 'Harvest', 'Fermentation', 'Aging', 'Bottling', 'Distribution'].map((step, index) => {
                          const isActive = index < 6;
                          return (
                            <div key={`economy-${step}`} className="z-10 flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow ${
                                isActive ? 'bg-amber-500 text-white' : 'bg-white text-monday-text-muted border border-monday-border-light'
                              }`}>
                                {index + 1}
                              </div>
                              <span className={`mt-2 text-xs ${isActive ? 'text-amber-700 font-medium' : 'text-monday-text-muted'}`}>{step}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parallel Wine Production Batches */}
              <div className="mt-12 bg-white p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-monday-text mb-4 flex items-center">
                  {FiRefreshCw({ className: "mr-2 text-primary-500", size: 20 })}
                  Parallel Production Processes
                </h3>
                <p className="text-monday-text-muted mb-6">
                  Multiple batches of wine are being processed simultaneously across different stages of production.
                </p>

                <div className="space-y-5">
                  {/* Production Batch Cards showing parallel activities */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200 shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-md font-semibold text-monday-text">Premium Cabernet</h4>
                        <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-medium rounded-full">Batch #23P-012</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span className="text-sm font-medium">Aging (Oak Barrels)</span>
                      </div>
                      <div className="flex justify-between text-xs text-monday-text-muted mb-3">
                        <span>Started: March 15, 2023</span>
                        <span>Est. completion: August 2024</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-1.5 mb-1">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '40%' }}></div>
                      </div>
                      <div className="text-xs text-right text-monday-text-muted">40% Complete</div>
                    </div>

                    <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4 border border-primary-200 shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-md font-semibold text-monday-text">Standard Merlot</h4>
                        <span className="px-2 py-1 bg-primary-200 text-primary-700 text-xs font-medium rounded-full">Batch #23S-008</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-3 h-3 rounded-full bg-primary-500 mr-2"></div>
                        <span className="text-sm font-medium">Fermentation (Secondary)</span>
                      </div>
                      <div className="flex justify-between text-xs text-monday-text-muted mb-3">
                        <span>Started: May 22, 2023</span>
                        <span>Est. completion: January 2024</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-1.5 mb-1">
                        <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                      <div className="text-xs text-right text-monday-text-muted">65% Complete</div>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200 shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-md font-semibold text-monday-text">Economy Blend</h4>
                        <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-medium rounded-full">Batch #23E-015</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                        <span className="text-sm font-medium">Quality Control</span>
                      </div>
                      <div className="flex justify-between text-xs text-monday-text-muted mb-3">
                        <span>Started: February 10, 2023</span>
                        <span>Est. completion: October 2023</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-1.5 mb-1">
                        <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      <div className="text-xs text-right text-monday-text-muted">85% Complete</div>
                    </div>
                  </div>

                  {/* Active Process Matrix */}
                  <div className="mt-8 overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                      <thead className="bg-monday-header">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Batch ID</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Wine Type</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Current Stage</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Start Date</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Location</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-monday-border-light">
                        <tr>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-monday-text">#23P-012</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Premium Cabernet</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Aging</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">March 15, 2023</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Barrel Room A, Row 3</td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-success-500 text-white">On Track</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-monday-text">#23S-008</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Standard Merlot</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Fermentation</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">May 22, 2023</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Fermentation Tank F12</td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-primary-100 text-primary-700">In Progress</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-monday-text">#23E-015</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Economy Blend</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Quality Control</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">February 10, 2023</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Lab Section B</td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">Final Testing</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-monday-text">#23P-014</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Premium Chardonnay</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Bottling</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">April 3, 2023</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Bottling Line 2</td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">Bottling</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-monday-text">#22S-042</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Standard Rosé</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Distribution</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">October 18, 2022</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-monday-text-muted">Warehouse C</td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-info-500 text-white">Shipping</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Weekly Process Activity Visualization */}
                  <div className="mt-8 bg-white p-4 rounded-lg border border-monday-border-light">
                    <h4 className="text-md font-semibold text-monday-text mb-3">Weekly Process Activity</h4>
                    <div className="grid grid-cols-7 gap-1">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <div key={day} className="text-xs text-center font-medium text-monday-text-muted">{day}</div>
                      ))}

                      {/* Premium Line Activities */}
                      <div className="col-span-7 mt-2 mb-1">
                        <div className="text-xs font-medium text-green-700 mb-1">Premium Line</div>
                        <div className="grid grid-cols-7 gap-1">
                          <div className="h-6 rounded bg-green-100 border border-green-300"></div>
                          <div className="h-6 rounded bg-green-200 border border-green-300 relative" title="Oak aging">
                            <span className="absolute inset-0 flex items-center justify-center text-xs text-green-800">Oak</span>
                          </div>
                          <div className="h-6 rounded bg-green-200 border border-green-300"></div>
                          <div className="h-6 rounded bg-green-200 border border-green-300"></div>
                          <div className="h-6 rounded bg-green-200 border border-green-300"></div>
                          <div className="h-6 rounded bg-green-100 border border-green-300"></div>
                          <div className="h-6 rounded bg-white border border-dashed border-monday-border"></div>
                        </div>
                      </div>

                      {/* Standard Line Activities */}
                      <div className="col-span-7 mb-1">
                        <div className="text-xs font-medium text-primary-600 mb-1">Standard Line</div>
                        <div className="grid grid-cols-7 gap-1">
                          <div className="h-6 rounded bg-primary-100 border border-primary-200"></div>
                          <div className="h-6 rounded bg-primary-200 border border-primary-200 relative" title="Fermentation">
                            <span className="absolute inset-0 flex items-center justify-center text-xs text-primary-700">Ferm</span>
                          </div>
                          <div className="h-6 rounded bg-primary-200 border border-primary-200"></div>
                          <div className="h-6 rounded bg-primary-100 border border-primary-200"></div>
                          <div className="h-6 rounded bg-primary-200 border border-primary-200 relative" title="Testing">
                            <span className="absolute inset-0 flex items-center justify-center text-xs text-primary-700">Test</span>
                          </div>
                          <div className="h-6 rounded bg-primary-200 border border-primary-200"></div>
                          <div className="h-6 rounded bg-white border border-dashed border-monday-border"></div>
                        </div>
                      </div>

                      {/* Economy Line Activities */}
                      <div className="col-span-7">
                        <div className="text-xs font-medium text-amber-700 mb-1">Economy Line</div>
                        <div className="grid grid-cols-7 gap-1">
                          <div className="h-6 rounded bg-amber-200 border border-amber-300 relative" title="Quality Control">
                            <span className="absolute inset-0 flex items-center justify-center text-xs text-amber-800">QC</span>
                          </div>
                          <div className="h-6 rounded bg-amber-200 border border-amber-300"></div>
                          <div className="h-6 rounded bg-amber-100 border border-amber-300"></div>
                          <div className="h-6 rounded bg-amber-300 border border-amber-400 relative" title="Bottling Prep">
                            <span className="absolute inset-0 flex items-center justify-center text-xs text-amber-800">Prep</span>
                          </div>
                          <div className="h-6 rounded bg-amber-300 border border-amber-400"></div>
                          <div className="h-6 rounded bg-amber-100 border border-amber-300"></div>
                          <div className="h-6 rounded bg-white border border-dashed border-monday-border"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Milestones Section */}
          <div className="bg-white rounded-lg shadow-lg relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-50 rounded-full"></div>
            <div className="absolute -bottom-20 -left-10 w-60 h-60 bg-yellow-50 rounded-full"></div>

            <div className="p-6 relative z-10">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-primary-100 rounded-lg text-primary-500 mr-3">
                  {FiTrendingUp({ size: 20 })}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-monday-text">Key Milestones</h2>
                  <div className="w-12 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full mt-1"></div>
                </div>
              </div>

              <div className="relative">
                {/* Timeline connector */}
                <div className="absolute left-9 top-0 bottom-0 w-1 bg-gradient-to-b from-green-300 via-primary-300 to-amber-300"></div>

                {/* Milestone cards */}
                <div className="space-y-8">
                  {milestones.map((milestone, index) => {
                    return (
                      <div
                        key={index}
                        className={`relative flex items-start pl-12 transition-all duration-700 ${
                          animate ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                        }`}
                        style={{ transitionDelay: `${index * 150}ms` }}
                      >
                        <div className="absolute top-1 left-0 bg-white p-2 rounded-full z-10 shadow-md border-2 border-primary-100">
                          {milestone.icon({ size: 24, className: milestone.iconColor })}
                        </div>
                        <div className="bg-white rounded-lg p-5 w-full shadow-md border border-monday-border-light hover:shadow-xl transition-all duration-300 hover:border-primary-200">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3">
                            <h3 className="text-lg font-semibold text-monday-text mb-2 sm:mb-0">{milestone.title}</h3>
                            <span className="text-sm font-medium text-monday-text-muted flex items-center px-3 py-1 bg-monday-header rounded-full">
                              {FiCalendar({ size: 14, className: "mr-1" })}
                              {milestone.date}
                            </span>
                          </div>
                          <div className="mb-3">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full inline-flex items-center ${
                              milestone.status === 'Completed' ? 'bg-success-500 text-white' :
                              milestone.status === 'In Progress' ? 'bg-primary-100 text-primary-700' :
                              milestone.status === 'Upcoming' ? 'bg-amber-100 text-amber-800' :
                              'bg-monday-header text-monday-text'
                            }`}>
                              {milestone.status === 'Completed' && FiCheckCircle({ size: 12, className: "mr-1" })}
                              {milestone.status === 'In Progress' && FiClock({ size: 12, className: "mr-1" })}
                              {milestone.status === 'Upcoming' && FiTarget({ size: 12, className: "mr-1" })}
                              {milestone.status === 'Target' && FiFlag({ size: 12, className: "mr-1" })}
                              {milestone.status}
                            </span>
                          </div>
                          <p className="text-monday-text-muted mb-3">{milestone.description}</p>

                          {/* Collapsible content */}
                          <details className="group">
                            <summary className="list-none flex items-center text-sm text-primary-500 font-medium cursor-pointer hover:text-primary-600 transition-colors duration-200">
                              <span className="flex items-center">
                                View details
                                {FiChevronRight({ size: 16, className: "ml-1 group-open:rotate-90 transition-transform duration-200" })}
                              </span>
                            </summary>
                            <div className="mt-3 pt-3 border-t border-monday-border-light animate-fadeIn">
                              <p className="text-monday-text-muted text-sm mb-3">{milestone.details}</p>

                              <h4 className="text-sm font-medium text-monday-text mb-2">Key Achievements:</h4>
                              <ul className="space-y-2 mb-0">
                                {milestone.achievements.map((achievement, i) => (
                                  <li key={i} className="flex items-start bg-primary-50 p-2 rounded-md">
                                    {FiArrowRight({ size: 14, className: "text-primary-500 mt-1 mr-2 flex-shrink-0" })}
                                    <span className="text-sm text-monday-text-muted">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </details>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Production Goals Section */}
          <div className="bg-gradient-to-br from-primary-50 via-white to-primary-50 rounded-lg shadow-lg relative overflow-hidden">
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary-100 rounded-full mix-blend-multiply filter blur-md opacity-50"></div>
            <div className="absolute top-0 left-10 w-20 h-20 bg-purple-100 rounded-full mix-blend-multiply filter blur-md opacity-40"></div>

            <div className="p-6 relative z-10">
              <div className="flex items-center mb-8">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg text-white mr-3 shadow-md">
                  {FiTarget({ size: 20 })}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-monday-text">Production Goals</h2>
                  <div className="w-12 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full mt-1"></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {productionGoals.map((goal, index) => (
                  <div
                    key={index}
                    className={`bg-white rounded-lg p-6 border border-monday-border-light transform transition-all duration-700 hover:-translate-y-2 hover:shadow-xl ${
                      animate ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                    }`}
                    style={{
                      transitionDelay: `${index * 100}ms`,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    {/* Goal card header with gradient background */}
                    <div className={`-mx-6 -mt-6 mb-6 p-4 bg-gradient-to-r
                      ${index === 0 ? 'from-primary-500 to-primary-600' :
                        index === 1 ? 'from-emerald-500 to-teal-600' :
                        'from-amber-500 to-orange-600'
                      } rounded-t-lg text-white flex justify-between items-center`}>
                      <div className="flex items-center">
                        <div className="p-2 bg-white bg-opacity-25 rounded-lg mr-3">
                          {goal.icon({ size: 20, className: "text-white" })}
                        </div>
                        <h3 className="text-lg font-medium">{goal.title}</h3>
                      </div>
                      <span className="text-2xl font-bold">{goal.value}</span>
                    </div>

                    <p className="text-monday-text-muted mb-5">{goal.description}</p>

                    {/* Progress circle indicator */}
                    <div className="mb-4 flex justify-center">
                      <div className="relative w-24 h-24">
                        {/* Background circle */}
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                          <circle
                            className="text-neutral-200"
                            strokeWidth="8"
                            stroke="currentColor"
                            fill="transparent"
                            r="40"
                            cx="50"
                            cy="50"
                          />
                          {/* Progress circle with animation */}
                          <circle
                            className={`${
                              index === 0 ? 'text-primary-500' :
                              index === 1 ? 'text-emerald-500' :
                              'text-amber-500'
                            } transition-all duration-1000 ease-out`}
                            strokeWidth="8"
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="40"
                            cx="50"
                            cy="50"
                            strokeDasharray="251.2"
                            strokeDashoffset={251.2 - (251.2 * goal.progress) / 100}
                            style={{
                              transformOrigin: 'center',
                              transform: 'rotate(-90deg)'
                            }}
                          />
                        </svg>

                        {/* Percentage in center */}
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                          <span className={`text-xl font-bold ${
                            index === 0 ? 'text-primary-500' :
                            index === 1 ? 'text-emerald-600' :
                            'text-amber-600'
                          }`}>{goal.progress}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Footer with project target info */}
                    <div className="mt-5 pt-3 border-t border-monday-border-light">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center text-sm text-monday-text-muted">
                          <span className="mr-2">Target:</span>
                          <span className="font-medium">{goal.projectedTarget.value}</span>
                        </div>
                        <div className="flex items-center text-sm text-monday-text-muted">
                          <span className="mr-2">By:</span>
                          <div className="flex items-center">
                            {FiCalendar({ size: 12, className: "mr-1 text-primary-500" })}
                            <span className="font-medium">{goal.projectedTarget.year}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick stats row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light flex items-center">
                  {FaWineBottle({ size: 18, className: "mr-3 text-primary-500" })}
                  <div>
                    <div className="text-sm text-monday-text-muted">Total Batches</div>
                    <div className="text-xl font-bold">26</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light flex items-center">
                  {FaWineGlass({ size: 18, className: "mr-3 text-purple-500" })}
                  <div>
                    <div className="text-sm text-monday-text-muted">Aging Barrels</div>
                    <div className="text-xl font-bold">142</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light flex items-center">
                  {FiDroplet({ size: 18, className: "mr-3 text-emerald-500" })}
                  <div>
                    <div className="text-sm text-monday-text-muted">Fermentation</div>
                    <div className="text-xl font-bold">8</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light flex items-center">
                  {FiAward({ size: 18, className: "mr-3 text-amber-500" })}
                  <div>
                    <div className="text-sm text-monday-text-muted">Bottled Cases</div>
                    <div className="text-xl font-bold">1,280</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'quality' && (
        <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 rounded-lg shadow-lg relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-100 rounded-full mix-blend-multiply filter blur-md opacity-50"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-emerald-100 rounded-full mix-blend-multiply filter blur-md opacity-50"></div>

          <div className="p-6 relative z-10">
            <div className="flex items-center mb-6">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg text-white mr-3 shadow-md">
                {FiAward({ size: 20 })}
              </div>
              <div>
                <h2 className="text-xl font-bold text-monday-text">Wine Quality Metrics</h2>
                <div className="w-12 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mt-1"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Distribution chart - takes up 2 columns */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-5 transform transition-all duration-500 hover:shadow-lg">
                <h3 className="text-lg font-semibold text-monday-text mb-4">Quality Distribution</h3>
                <div className="h-80">
                  <ResponsiveChart type="doughnut" data={qualityData as any} options={qualityOptions as any} height="320px" variant="embedded" />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center justify-center bg-green-50 rounded-md p-2">
                    <div className="font-medium text-green-700">Premium</div>
                    <div className="text-lg font-bold text-green-800">60%</div>
                  </div>
                  <div className="flex flex-col items-center justify-center bg-primary-50 rounded-md p-2">
                    <div className="font-medium text-primary-600">Standard</div>
                    <div className="text-lg font-bold text-primary-700">30%</div>
                  </div>
                  <div className="flex flex-col items-center justify-center bg-amber-50 rounded-md p-2">
                    <div className="font-medium text-amber-700">Economy</div>
                    <div className="text-lg font-bold text-amber-800">10%</div>
                  </div>
                </div>
              </div>

              {/* Quality cards - takes up 3 columns */}
              <div className="lg:col-span-3 space-y-5">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow-md border border-green-200 overflow-hidden transform transition-all duration-300 hover:shadow-lg">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-monday-text flex items-center">
                        {FiAward({ size: 18, className: "mr-2 text-green-600" })}
                        Premium Line
                      </h3>
                      <div className="px-3 py-1 rounded-full bg-white text-green-700 font-medium text-sm shadow-sm">
                        92/100
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      <div className="w-full bg-white rounded-full h-2.5 mr-2">
                        <div className="bg-gradient-to-r from-green-400 to-green-600 h-2.5 rounded-full animate-expandWidth" style={{ width: '92%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-green-700">92%</span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Acidity</p>
                        <div className="flex justify-center items-center">
                          {FiDroplet({ size: 14, className: "mr-1 text-green-500" })}
                          <p className="font-medium">3.4 pH</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Alcohol</p>
                        <div className="flex justify-center items-center">
                          {FaWineGlass({ size: 14, className: "mr-1 text-green-500" })}
                          <p className="font-medium">14.2%</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Tannin</p>
                        <div className="flex justify-center items-center">
                          {FaLeaf({ size: 14, className: "mr-1 text-green-500" })}
                          <p className="font-medium">High</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-white rounded-md">
                      <h4 className="text-sm font-medium text-monday-text mb-2">Tasting Notes:</h4>
                      <p className="text-sm text-monday-text-muted">Rich and complex with notes of black cherry, vanilla, and oak. Full-bodied with a long finish.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg shadow-md border border-primary-200 overflow-hidden transform transition-all duration-300 hover:shadow-lg">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-monday-text flex items-center">
                        {FiCheckCircle({ size: 18, className: "mr-2 text-primary-500" })}
                        Standard Line
                      </h3>
                      <div className="px-3 py-1 rounded-full bg-white text-primary-600 font-medium text-sm shadow-sm">
                        86/100
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      <div className="w-full bg-white rounded-full h-2.5 mr-2">
                        <div className="bg-gradient-to-r from-primary-400 to-primary-600 h-2.5 rounded-full animate-expandWidth" style={{ width: '86%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-primary-600">86%</span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Acidity</p>
                        <div className="flex justify-center items-center">
                          {FiDroplet({ size: 14, className: "mr-1 text-primary-500" })}
                          <p className="font-medium">3.6 pH</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Alcohol</p>
                        <div className="flex justify-center items-center">
                          {FaWineGlass({ size: 14, className: "mr-1 text-primary-500" })}
                          <p className="font-medium">13.5%</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Tannin</p>
                        <div className="flex justify-center items-center">
                          {FaLeaf({ size: 14, className: "mr-1 text-primary-500" })}
                          <p className="font-medium">Medium</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-white rounded-md">
                      <h4 className="text-sm font-medium text-monday-text mb-2">Tasting Notes:</h4>
                      <p className="text-sm text-monday-text-muted">Medium-bodied with balanced flavors of red fruits and subtle spice. Approachable with moderate complexity.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg shadow-md border border-amber-200 overflow-hidden transform transition-all duration-300 hover:shadow-lg">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-monday-text flex items-center">
                        {FiTarget({ size: 18, className: "mr-2 text-amber-600" })}
                        Economy Line
                      </h3>
                      <div className="px-3 py-1 rounded-full bg-white text-amber-700 font-medium text-sm shadow-sm">
                        78/100
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      <div className="w-full bg-white rounded-full h-2.5 mr-2">
                        <div className="bg-gradient-to-r from-amber-400 to-amber-600 h-2.5 rounded-full animate-expandWidth" style={{ width: '78%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-amber-700">78%</span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Acidity</p>
                        <div className="flex justify-center items-center">
                          {FiDroplet({ size: 14, className: "mr-1 text-amber-500" })}
                          <p className="font-medium">3.7 pH</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Alcohol</p>
                        <div className="flex justify-center items-center">
                          {FaWineGlass({ size: 14, className: "mr-1 text-amber-500" })}
                          <p className="font-medium">12.8%</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-md p-3 shadow-sm">
                        <p className="text-xs text-monday-text-muted mb-1">Tannin</p>
                        <div className="flex justify-center items-center">
                          {FaLeaf({ size: 14, className: "mr-1 text-amber-500" })}
                          <p className="font-medium">Low</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-white rounded-md">
                      <h4 className="text-sm font-medium text-monday-text mb-2">Tasting Notes:</h4>
                      <p className="text-sm text-monday-text-muted">Light-bodied and fruit-forward with easy drinking characteristics. Simple flavor profile focused on freshness.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Quality Metrics */}
            <div className="mt-8 bg-white rounded-lg shadow-md p-5">
              <h3 className="text-lg font-semibold text-monday-text mb-4 flex items-center">
                {FiRefreshCw({ className: "mr-2 text-primary-500", size: 20 })}
                Quality Improvement Tracking
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-monday-header rounded-lg p-4">
                  <h4 className="text-md font-medium text-monday-text mb-2">Vintage Comparison</h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-monday-text-muted">2021 Vintage</span>
                    <span className="text-sm font-medium text-primary-500">82/100</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-1.5 mb-4">
                    <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: '82%' }}></div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-monday-text-muted">2022 Vintage</span>
                    <span className="text-sm font-medium text-primary-500">86/100</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-1.5 mb-4">
                    <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: '86%' }}></div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-monday-text-muted">2023 Vintage</span>
                    <span className="text-sm font-medium text-primary-500">92/100</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-1.5">
                    <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                </div>

                <div className="bg-monday-header rounded-lg p-4">
                  <h4 className="text-md font-medium text-monday-text mb-2">Quality Control Tests</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        {FiCheckCircle({ size: 14, className: "mr-2 text-green-500" })}
                        <span className="text-sm text-monday-text-muted">Chemical Analysis</span>
                      </div>
                      <span className="text-xs bg-success-500 text-white px-2 py-0.5 rounded-full">Completed</span>
                    </div>

                    <div className="flex justify-between items-center">
                      {FiCheckCircle({ size: 14, className: "mr-2 text-green-500" })}
                      <span className="text-sm text-monday-text-muted">Microbial Testing</span>
                      <span className="text-xs bg-success-500 text-white px-2 py-0.5 rounded-full">Completed</span>
                    </div>

                    <div className="flex justify-between items-center">
                      {FiCheckCircle({ size: 14, className: "mr-2 text-green-500" })}
                      <span className="text-sm text-monday-text-muted">Sensory Evaluation</span>
                      <span className="text-xs bg-success-500 text-white px-2 py-0.5 rounded-full">Completed</span>
                    </div>

                    <div className="flex justify-between items-center">
                      {FiClock({ size: 14, className: "mr-2 text-amber-500" })}
                      <span className="text-sm text-monday-text-muted">Aging Potential Assessment</span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">In Progress</span>
                    </div>
                  </div>
                </div>

                <div className="bg-monday-header rounded-lg p-4">
                  <h4 className="text-md font-medium text-monday-text mb-2">External Recognition</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      {FiAward({ size: 16, className: "mr-2 text-yellow-500" })}
                      <div>
                        <p className="text-sm font-medium text-monday-text">Gold Medal</p>
                        <p className="text-xs text-monday-text-muted">Regional Wine Competition 2023</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      {FiAward({ size: 16, className: "mr-2 text-monday-text-muted" })}
                      <div>
                        <p className="text-sm font-medium text-monday-text">Silver Medal</p>
                        <p className="text-xs text-monday-text-muted">International Wine Challenge</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      {FiSun({ size: 16, className: "mr-2 text-amber-500" })}
                      <div>
                        <p className="text-sm font-medium text-monday-text">92 Points</p>
                        <p className="text-xs text-monday-text-muted">Wine Enthusiast Magazine</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-gradient-to-br from-primary-50 via-white to-primary-50 rounded-lg shadow-lg relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-100 rounded-full mix-blend-multiply filter blur-md opacity-40"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-100 rounded-full mix-blend-multiply filter blur-md opacity-40"></div>

          <div className="p-6 relative z-10">
            <div className="flex items-center mb-6">
              <div className="p-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg text-white mr-3 shadow-md">
                {FiActivity({ size: 20 })}
              </div>
              <div>
                <h2 className="text-xl font-bold text-monday-text">Recent Activities</h2>
                <div className="w-12 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full mt-1"></div>
              </div>
            </div>

            {/* Activity stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light group hover:shadow-md transition-all duration-300 hover:border-primary-200">
                <div className="flex items-center">
                  <div className="p-2 bg-primary-100 rounded-lg mr-3 group-hover:bg-primary-200 transition-colors duration-300">
                    {FiTarget({ size: 18, className: "text-primary-500" })}
                  </div>
                  <div>
                    <div className="text-xs text-monday-text-muted">Today</div>
                    <div className="text-lg font-bold text-monday-text">8</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light group hover:shadow-md transition-all duration-300 hover:border-primary-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors duration-300">
                    {FiClock({ size: 18, className: "text-green-600" })}
                  </div>
                  <div>
                    <div className="text-xs text-monday-text-muted">This Week</div>
                    <div className="text-lg font-bold text-monday-text">32</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light group hover:shadow-md transition-all duration-300 hover:border-primary-200">
                <div className="flex items-center">
                  <div className="p-2 bg-amber-100 rounded-lg mr-3 group-hover:bg-amber-200 transition-colors duration-300">
                    {FiCalendar({ size: 18, className: "text-amber-600" })}
                  </div>
                  <div>
                    <div className="text-xs text-monday-text-muted">This Month</div>
                    <div className="text-lg font-bold text-monday-text">128</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-monday-border-light group hover:shadow-md transition-all duration-300 hover:border-primary-200">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg mr-3 group-hover:bg-purple-200 transition-colors duration-300">
                    {FiCheckCircle({ size: 18, className: "text-purple-600" })}
                  </div>
                  <div>
                    <div className="text-xs text-monday-text-muted">Completed</div>
                    <div className="text-lg font-bold text-monday-text">94%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="mb-5 flex space-x-1 bg-monday-header p-1 rounded-lg">
              {['All Activities', 'Vineyard', 'Fermentation', 'Aging', 'Quality Control'].map((filter, index) => (
                <button
                  key={filter}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    index === 0 ? 'bg-white text-primary-600 shadow-sm' : 'text-monday-text-muted hover:bg-monday-hover'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Activity timeline */}
            <div className="space-y-4 relative">
              {/* Vertical timeline connector */}
              <div className="absolute top-0 bottom-0 left-16 w-0.5 bg-gradient-to-b from-primary-300 via-primary-300 to-primary-300 z-0"></div>

              {recentActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className={`relative z-10 transition-all duration-500 ${
                    animate ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  {/* Date separator - show before the first item or when date changes */}
                  {index === 0 && (
                    <div className="flex items-center mb-4">
                      <div className="bg-primary-100 px-3 py-1 rounded-full text-primary-600 text-sm font-medium">Today</div>
                      <div className="flex-1 h-px bg-neutral-200 ml-3"></div>
                    </div>
                  )}

                  {/* Activity Card */}
                  <div className="flex items-start">
                    {/* Time column */}
                    <div className="w-16 text-right pr-4 text-xs text-monday-text-muted pt-2">
                      {activity.timestamp.includes('hour') ?
                        activity.timestamp.replace(' ago', '') :
                        '12:45 PM'
                      }
                    </div>

                    {/* Icon with circle background */}
                    <div className="relative">
                      <div className={`p-2 rounded-full shadow-md z-10 ${
                        activity.type === 'treatment' ? 'bg-emerald-500' :
                        activity.type === 'fermentation' ? 'bg-primary-500' :
                        activity.type === 'quality' ? 'bg-blue-500' :
                        'bg-amber-500'
                      }`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full animate-ping-slow opacity-20 ${
                        activity.type === 'treatment' ? 'bg-emerald-500' :
                        activity.type === 'fermentation' ? 'bg-primary-500' :
                        activity.type === 'quality' ? 'bg-blue-500' :
                        'bg-amber-500'
                      }`}></div>
                    </div>

                    {/* Content */}
                    <div className="ml-4 flex-1">
                      <div className="bg-white rounded-lg p-4 border border-monday-border-light shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary-200">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2">
                          <h3 className="text-md font-medium text-monday-text">{activity.action}</h3>
                          <span className="text-xs bg-monday-header text-monday-text-muted px-2 py-1 rounded-full md:ml-2 flex-shrink-0 md:mt-0 mt-1 inline-flex items-center">
                            {activity.type === 'treatment' && "Vineyard Treatment"}
                            {activity.type === 'fermentation' && "Fermentation"}
                            {activity.type === 'quality' && "Quality Control"}
                            {activity.type === 'aging' && "Aging Process"}
                          </span>
                        </div>
                        <p className="text-sm text-monday-text-muted flex items-center">
                          <span className="inline-block w-6 h-6 bg-primary-100 rounded-full mr-2 overflow-hidden flex-shrink-0">
                            <span className="text-xs font-medium text-primary-600 flex items-center justify-center h-full">
                              {activity.user.split(' ')[0][0]}{activity.user.split(' ')[1][0]}
                            </span>
                          </span>
                          {activity.user}
                        </p>

                        {/* Action buttons */}
                        <div className="mt-3 flex space-x-2">
                          <button className="text-xs text-primary-500 hover:text-primary-700 font-medium flex items-center">
                            {FiCheckCircle({ size: 12, className: "mr-1" })} Mark reviewed
                          </button>
                          <button className="text-xs text-monday-text-muted hover:text-monday-text font-medium flex items-center">
                            {FiActivity({ size: 12, className: "mr-1" })} View details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add more separator for yesterday */}
              <div className="relative z-10">
                <div className="flex items-center my-4">
                  <div className="bg-monday-header px-3 py-1 rounded-full text-monday-text text-sm font-medium">Yesterday</div>
                  <div className="flex-1 h-px bg-neutral-200 ml-3"></div>
                </div>

                {/* Sample yesterday activity */}
                <div className="flex items-start">
                  <div className="w-16 text-right pr-4 text-xs text-monday-text-muted pt-2">
                    4:30 PM
                  </div>
                  <div className="relative">
                    <div className="p-2 rounded-full shadow-md z-10 bg-monday-header0">
                      {FiGlobe({ size: 18, className: "text-white" })}
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="bg-white rounded-lg p-4 border border-monday-border-light shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary-200">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2">
                        <h3 className="text-md font-medium text-monday-text">Weather station data updated</h3>
                        <span className="text-xs bg-monday-header text-monday-text-muted px-2 py-1 rounded-full md:ml-2 flex-shrink-0 md:mt-0 mt-1 inline-flex items-center">
                          System
                        </span>
                      </div>
                      <p className="text-sm text-monday-text-muted">Automatic weather data sync from vineyard sensors</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button className="w-full py-3 bg-gradient-to-r from-primary-50 to-primary-50 border border-primary-200 rounded-lg font-medium text-primary-500 hover:from-primary-100 hover:to-primary-100 transition-colors duration-200 flex items-center justify-center group">
                  <span>View All Activities</span>
                  {FiArrowRight({ size: 14, className: "ml-2 transform transition-transform duration-200 group-hover:translate-x-1" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add keyframe animations to the style section */}
      <style>{`
        @keyframes blob {
          0% {
            transform: scale(1);
          }
          33% {
            transform: scale(1.1);
          }
          66% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes ping-slow {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.2;
          }
          100% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0.3;
          }
        }

        @keyframes expandWidth {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        .animate-expandWidth {
          animation: expandWidth 1.5s ease-out forwards;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Tracking;
